import type { ReactNode } from 'react';

import diabetesDexcomG7Sensor from '@/assets/product-categories/diabetes/dexcom-g7-sensor.png';
import diabetesFreestyleLibre3 from '@/assets/product-categories/diabetes/freestyle-libre-3.png';
import diabetesLancet from '@/assets/product-categories/diabetes/lancet.png';
import accessibilityHospitalBed from '@/assets/product-categories/home-accessibility/hospital-bed.png';
import accessibilityOverbedTable from '@/assets/product-categories/home-accessibility/overbed-table.png';
import accessibilityRaisedToiletSeat from '@/assets/product-categories/home-accessibility/raised-toilet-seat.png';
import incontinenceDisposableUnderwear from '@/assets/product-categories/incontinence/disposable-underwear.png';
import incontinenceLinerPad from '@/assets/product-categories/incontinence/liner-pad.png';
import incontinenceUnderpad from '@/assets/product-categories/incontinence/underpad.png';
import mobilityRollator from '@/assets/product-categories/mobility/rollator.png';
import mobilityWalker from '@/assets/product-categories/mobility/walker.png';
import mobilityWheelchair from '@/assets/product-categories/mobility/wheelchair.png';
import { type Step, type TanstackUrl, getCurrentStepIndex } from '@/lib/stepper.ts';
import { CATEGORY_TYPE, type CategoryType, type ProductCategory } from '@/types/product.type.ts';

export type CategorySelectionCard = {
  /** Card order on `/0`, which differs from the product step order. */
  order: number;
  /** Differs from the backend label for Home Accessibility. */
  displayLabel: string;
  description: string;
  images: string[];
};

export type ProductStep = Step & {
  categoryType?: CategoryType;
  selectionMode?: 'single' | 'multi';
  /** Not `title`/`description`: `Step.description` is the stepper's `n of m` suffix. */
  pageTitle?: ReactNode;
  pageDescription?: ReactNode;
  /** Absent means the category is not offered at `/0`, so nothing there can remove it. */
  onCategorySelection?: CategorySelectionCard;
};

export const PRODUCT_STEP_ID_SUMMARY = 'Summary';
export const PRODUCT_STEP_URL_SUMMARY = '/products/summary';
export const PRODUCT_STEP_URL_NO_PRODUCTS_SELECTED = '/products/no-products-selected';

/**
 * `id` must match the backend `ProductCategory.label` exactly. `url` stays explicit because the
 * shipped routes predate the labels: `Home Accessibility` lives at `/products/accessibility`.
 */
export const PRODUCT_STEPS: ProductStep[] = [
  {
    id: 'Incontinence',
    name: 'Incontinence',
    description: 'categories',
    url: '/products/incontinence',
    categoryType: CATEGORY_TYPE.INCONTINENCE,
    selectionMode: 'multi',
    pageTitle: 'Choose incontinence supplies',
    pageDescription: 'Refillable subscription—provider will help choose sizing & quantity.',
    onCategorySelection: {
      order: 1,
      displayLabel: 'Incontinence',
      description: 'Adult diapers, protective underwear, and pads',
      images: [incontinenceDisposableUnderwear, incontinenceLinerPad, incontinenceUnderpad],
    },
  },
  {
    id: 'Mobility',
    name: 'Mobility',
    description: 'categories',
    url: '/products/mobility',
    categoryType: CATEGORY_TYPE.MOBILITY,
    selectionMode: 'single',
    pageTitle: (
      <>
        Choose <span className="text-orchid-700">one</span> mobility aid
      </>
    ),
    pageDescription: (
      <>
        Medicare covers <b>only one</b> mobility aid <b>every 5 years</b>.
      </>
    ),
    onCategorySelection: {
      order: 0,
      displayLabel: 'Mobility',
      description: 'Wheelchairs, walkers, canes, and rollators',
      images: [mobilityWheelchair, mobilityRollator, mobilityWalker],
    },
  },
  {
    id: 'Diabetes',
    name: 'Diabetes',
    description: 'categories',
    url: '/products/diabetes',
    categoryType: CATEGORY_TYPE.DIABETES,
    selectionMode: 'single',
    pageTitle: 'Choose a diabetes glucose monitor',
    onCategorySelection: {
      order: 2,
      displayLabel: 'Diabetes',
      description: 'Blood glucose monitors, test strips, and lancets',
      images: [diabetesDexcomG7Sensor, diabetesFreestyleLibre3, diabetesLancet],
    },
  },
  {
    id: 'Home Accessibility',
    name: 'Accessibility',
    description: 'categories',
    url: '/products/accessibility',
    categoryType: CATEGORY_TYPE.ACCESSIBILITY,
    selectionMode: 'multi',
    pageTitle: 'Choose home accessibility aids',
    onCategorySelection: {
      order: 3,
      displayLabel: 'Home accessibility',
      description: 'Grab bars, shower chairs, hospital beds',
      images: [accessibilityHospitalBed, accessibilityRaisedToiletSeat, accessibilityOverbedTable],
    },
  },
  {
    id: 'Nutrition',
    name: 'Nutrition',
    description: 'categories',
    url: '/products/nutrition',
    categoryType: CATEGORY_TYPE.NUTRITION,
    selectionMode: 'single',
    pageTitle: 'Choose a nutrition supplement',
    pageDescription: 'Refillable subscription—provider will help choose dosage & quantity.',
  },
  {
    id: 'General Health',
    name: 'General Health',
    description: 'categories',
    url: '/products/general-health',
    categoryType: CATEGORY_TYPE.GENERAL_HEALTH,
    selectionMode: 'multi',
    pageTitle: 'Choose general health supplies',
  },
  {
    id: PRODUCT_STEP_ID_SUMMARY,
    name: 'Summary',
    url: PRODUCT_STEP_URL_SUMMARY,
    hideStepCount: true,
  },
];

/** Excludes the Summary pseudo-step, which has no `categoryType`. */
export function getCategoryStep(label: string): ProductStep | undefined {
  const step = PRODUCT_STEPS.find((candidate) => candidate.id === label);
  return step?.categoryType ? step : undefined;
}

export function hasProductStep(label: string): boolean {
  return getCategoryStep(label) != null;
}

export function getCategorySelectionCard(label: string): CategorySelectionCard | undefined {
  return getCategoryStep(label)?.onCategorySelection;
}

export function isCategorySelectionCard(label: string): boolean {
  return getCategorySelectionCard(label) != null;
}

export function getProductStepByUrl(url: string): ProductStep | undefined {
  return PRODUCT_STEPS.find((step) => step.url === url);
}

/** Map persisted category UUIDs to `ProductCategory.label` values (aligned with `ProductStep.id`). */
export function categoryIdsToLabels(
  ids: string[],
  categories: ProductCategory[] | undefined
): string[] {
  if (!categories?.length || !ids.length) return [];
  const byUuid = new Map(categories.map((c) => [c.uuid, c.label]));
  return ids.map((id) => byUuid.get(id)).filter((label): label is string => label != null);
}

type ActiveProductStepsInput = {
  visibleLabels: Set<string>;
  /** `undefined` until the catalog loads, which skips the eligibility gate. */
  eligibleLabels: Set<string> | undefined;
  selectedLabels: Set<string>;
};

export function getActiveProductSteps({
  visibleLabels,
  eligibleLabels,
  selectedLabels,
}: ActiveProductStepsInput): ProductStep[] {
  return PRODUCT_STEPS.filter((step) => {
    if (!step.categoryType) return false;
    if (!visibleLabels.has(step.id)) return false;
    if (eligibleLabels && !eligibleLabels.has(step.id)) return false;
    if (step.onCategorySelection && !selectedLabels.has(step.id)) return false;
    return true;
  });
}

export function getNextProductStepUrl(activeSteps: ProductStep[]): TanstackUrl {
  if (activeSteps.length === 0) return PRODUCT_STEP_URL_SUMMARY;

  const stepIndex = getCurrentStepIndex(activeSteps);
  if (stepIndex === -1) {
    if (window.location.pathname === PRODUCT_STEP_URL_SUMMARY) {
      return PRODUCT_STEP_URL_SUMMARY;
    }
    return activeSteps[0]!.url;
  }

  const following = activeSteps[stepIndex + 1];
  return following ? following.url : PRODUCT_STEP_URL_SUMMARY;
}
